import { gql } from "@apollo/client";

const FIND_ASSET_DOCUMENTS = gql`
  query DocumentsByAsset($assetId: ID!) {
    documentsByAsset(assetId: $assetId) {
      giftAgreementFee
      documents {
        _id
        createdAt
        fileName
        filePath
        fileCategory
        status
        executionDate
        signedBy
        fileType
        gridFsFileId
        updatedAt
        internalOnly
      }
    }
  }
`;

const FIND_TRANSACTION_DOCUMENTS = gql`
  query DocumentsByTransaction($transactionId: ID!) {
    documentsByTransaction(transactionId: $transactionId) {
      _id
      createdAt
      fileName
      filePath
      fileCategory
      fileType
      gridFsFileId
      updatedAt
      internalOnly
    }
  }
`;

const FIND_ENTITY_DOCUMENTS = gql`
  query DocumentsByEntity($entityId: ID!) {
    documentsByEntity(entityId: $entityId) {
      _id
      createdAt
      fileCategory
      fileName
      filePath
      fileType
      product {
        ... on DafProduct {
          _id
          name
          assets {
            ... on EquityAsset {
              _id
              assetName
            }
          }
        }
      }
      gridFsFileId
      updatedAt
      internalOnly
    }
  }
`;

const FIND_PRODUCT_SPECIFIC_DOCUMENTS = gql`
  query ProductSpecificDocuments($productId: ID!, $asExternalUser: Boolean) {
    productSpecificDocuments(productId: $productId, asExternalUser: $asExternalUser) {
      _id
      createdAt
      executionDate
      fileCategory
      fileName
      fileType
      gridFsFileId
      internalOnly
    }
  }
`;

const FIND_UNCATEGORIZED_DOCUMENTS = gql`
  query UncategorizedDocuments {
    uncategorizedDocuments {
      _id
      asset {
        ... on EquityAsset {
          _id
          assetName
        }
      }
      createdAt
      executionDate
      fileCategory
      fileName
      filePath
      fileType
      internalOnly
      product {
        ... on DafProduct {
          _id
          name
          assets {
            ... on EquityAsset {
              _id
              assetName
            }
          }
        }
      }
      createdBy {
        ... on InternalUser {
          _id
          firstName
          lastName
        }
        ... on DonorUser {
          _id
          firstName
          lastName
        }
        ... on FirmUser {
          _id
          firstName
          lastName
        }
        ... on EntityUser {
          _id
          firstName
          lastName
        }
      }
      gridFsFileId
      user {
        ... on InternalUser {
          _id
          firstName
          lastName
          type
        }
        ... on DonorUser {
          _id
          firstName
          lastName
          type
        }
        ... on FirmUser {
          _id
          firstName
          lastName
          type
        }
        ... on EntityUser {
          _id
          firstName
          lastName
          type
        }
      }
    }
  }
`;

const FIND_REQUESTED_AGREEMENTS = gql`
  query FindRequestedAgreements($productId: ID!) {
    findRequestedAgreements(productId: $productId) {
      _id
      executionDate
      fileCategory
      signedBy
      status
      asset {
        ... on EquityAsset {
          _id
          assetName
          assetType
          feeTransactions {
            _id
            feeType
            status
            requestedAmount
          }
        }
        ... on LendingAsset {
          _id
          assetName
          assetType
          feeTransactions {
            _id
            feeType
            status
            requestedAmount
          }
        }
      }
    }
  }
`;

const FIND_ALL_DOCUMENTS = gql`
  query AllDocuments($input: ServerQueryInput) {
    allDocuments(input: $input) {
      pageInfo {
        currentPage
        pageSize
        totalDocuments
        totalPages
      }
      documents {
        _id
        fileName
        fileCategory
        fileType
        internalOnly
        isGlobal
        exclusions {
          firmIds
          partnerIds
        }
        externalUpload
        uploadLinkToken
        uploadedByEmail
        signedBy
        executionDate
        status
        createdAt
        createdBy {
          ... on DonorUser { _id firstName lastName type }
          ... on InternalUser { _id firstName lastName type }
          ... on FirmUser { _id firstName lastName type }
          ... on EntityUser { _id firstName lastName type }
        }
        user {
          ... on DonorUser { _id firstName lastName type }
          ... on InternalUser { _id firstName lastName type }
          ... on FirmUser { _id firstName lastName type }
          ... on EntityUser { _id firstName lastName type }
        }
        entity {
          ... on FirmEntity { _id entityName entityType }
          ... on OrganizationEntity { _id entityName entityType }
          ... on CustodianEntity { _id entityName entityType }
          ... on PartnerEntity { _id entityName entityType }
        }
        asset {
          ... on EquityAsset { _id assetName assetType }
          ... on LendingAsset { _id assetName assetType }
          ... on AccountAsset { _id assetName assetType }
          ... on CryptoAsset { _id assetName assetType }
          ... on FundAsset { _id assetName assetType }
          ... on OtherAsset { _id assetName assetType }
          ... on RecoverableGrantAsset { _id assetName assetType }
          ... on OpportunityAsset { _id assetName assetType }
        }
        product {
          ... on DafProduct { _id name }
          ... on ClatProduct { _id name }
          ... on BrandingProduct { _id name }
          ... on CommunityFundProduct { _id name }
        }
        transaction {
          ... on CashContributionTransaction { _id transactionType transactionStatus }
          ... on EquityContributionTransaction { _id transactionType transactionStatus }
          ... on GrantTransaction { _id transactionType transactionStatus }
        }
        task {
          _id
          key
        }
      }
      totalCount
    }
  }
`;

const FIND_DUPLICATE_DOCUMENTS = gql`
  query DuplicateDocuments {
    duplicateDocuments {
      fileName
      fileType
      documents {
        _id
        fileName
        fileCategory
        fileType
        internalOnly
        createdAt
        user {
          ... on DonorUser { _id firstName lastName type }
          ... on InternalUser { _id firstName lastName type }
          ... on FirmUser { _id firstName lastName type }
          ... on EntityUser { _id firstName lastName type }
        }
        asset {
          ... on EquityAsset { _id assetName assetType }
          ... on LendingAsset { _id assetName assetType }
          ... on AccountAsset { _id assetName assetType }
          ... on CryptoAsset { _id assetName assetType }
          ... on FundAsset { _id assetName assetType }
          ... on OtherAsset { _id assetName assetType }
          ... on RecoverableGrantAsset { _id assetName assetType }
          ... on OpportunityAsset { _id assetName assetType }
        }
        entity {
          ... on FirmEntity { _id entityName entityType }
          ... on OrganizationEntity { _id entityName entityType }
          ... on CustodianEntity { _id entityName entityType }
          ... on PartnerEntity { _id entityName entityType }
        }
      }
    }
  }
`;

const FIND_ASSET_DOCUMENT_AUDIT = gql`
  query AssetDocumentAudit($assetType: String!, $acquisitionMethod: AssetAcquisitionMethodEnum, $search: String, $categoryFilters: [CategoryFilterInput], $skip: Int, $limit: Int) {
    assetDocumentAudit(assetType: $assetType, acquisitionMethod: $acquisitionMethod, search: $search, categoryFilters: $categoryFilters, skip: $skip, limit: $limit) {
      rows {
        asset {
          ... on EquityAsset { _id assetName assetType owner { _id firstName lastName } }
          ... on LendingAsset { _id assetName assetType owner { _id firstName lastName } }
          ... on AccountAsset { _id assetName assetType owner { _id firstName lastName } }
          ... on CryptoAsset { _id assetName assetType owner { _id firstName lastName } }
          ... on FundAsset { _id assetName assetType owner { _id firstName lastName } }
          ... on OtherAsset { _id assetName assetType owner { _id firstName lastName } }
          ... on RecoverableGrantAsset { _id assetName assetType owner { _id firstName lastName } }
          ... on OpportunityAsset { _id assetName assetType owner { _id firstName lastName } }
        }
        documentCategories
      }
      totalCount
    }
  }
`;

const FIND_GLOBAL_DOCUMENTS = gql`
  query GlobalDocuments($asUserId: ID) {
    globalDocuments(asUserId: $asUserId) {
      _id
      fileName
      fileCategory
      fileType
      gridFsFileId
      internalOnly
      isGlobal
      exclusions {
        firmIds
        partnerIds
      }
      createdAt
    }
  }
`;

export {
  FIND_ENTITY_DOCUMENTS,
  FIND_TRANSACTION_DOCUMENTS,
  FIND_PRODUCT_SPECIFIC_DOCUMENTS,
  FIND_ASSET_DOCUMENTS,
  FIND_UNCATEGORIZED_DOCUMENTS,
  FIND_REQUESTED_AGREEMENTS,
  FIND_ALL_DOCUMENTS,
  FIND_DUPLICATE_DOCUMENTS,
  FIND_ASSET_DOCUMENT_AUDIT,
  FIND_GLOBAL_DOCUMENTS,
};
